import { type Registry as Services, service } from '@ember/service';
import Model, {
  attr,
  belongsTo,
  type HasMany,
  hasMany,
} from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { Hotelinfo } from 'frontend-utils/models';
import type { Rsbaserate, Rssubtype } from 'lumen/models';
import { NO_DATA_SUBSORT } from 'lumen/utils/constants/rate-strategy-constants';

export default class Rspattern extends Model {
  [Type] = 'rspattern' as const;

  @belongsTo('hotelinfo', { async: false, inverse: null })
  declare hotelinfo: Hotelinfo;

  @belongsTo('rsbaserate', { async: false, inverse: null })
  declare rsbaserate: Rsbaserate;

  // OpenAPI spec: https://git.mylighthouse.com/OTA-Insight/pricing-insight/-/blob/master/api/openapi-spec/openapi.yaml
  // Pricing data described as a pattern. Will be used in the rate strategy charts
  @belongsTo('rssubtype', { async: true, inverse: 'rspatterns' })
  declare rssubtype: Rssubtype;

  @hasMany('rspattern', { async: false, inverse: null })
  declare rssubpatterns: HasMany<Rspattern>;

  @attr('string') declare label: string;
  @attr('string') declare message: string;
  @attr('number') declare rate: number;

  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedRate() {
    if (this.isSoldOut) {
      return this.intl.t('rates.soldout').toString();
    }

    if (this.hasNoData) {
      return this.intl.t('general.missing').toString();
    }

    return this.hotelAndMarketService.formatRate(this.rate, true, false);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedRoomname() {
    if (this.isSoldOut) {
      return this.intl.t('rates.soldout').toString();
    }

    if (this.hasNoData) {
      return this.intl.t('general.missing').toString();
    }

    return this.rsbaserate.roomname;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasNoData() {
    return (
      this.isMissing ||
      this.isSoldOut ||
      this.isNonComputable ||
      this.isNotOffered
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isExcludedFromPosition() {
    return this.hasNoData;
  }

  // Check if the rate is missing (may need to be updated once we have the warnings)
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMissing() {
    return this.message === 'general.missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isNonComputable() {
    return this.message === 'rates.noncomputable';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isNotOffered() {
    return this.message === 'rates.notoffered';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isSoldOut() {
    return this.message === 'rates.soldout';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get subsortOrder() {
    // When comparing two strategies with no data, subsort using the following order:
    // 1. Non-computable 2. Not offered 3. Soldout 4. Missing
    if (this.isNonComputable) {
      return NO_DATA_SUBSORT.NON_COMPUTABLE;
    }
    if (this.isNotOffered) {
      return NO_DATA_SUBSORT.NOT_OFFERED;
    }
    if (this.isSoldOut) {
      return NO_DATA_SUBSORT.SOLDOUT;
    }
    if (this.isMissing) {
      return NO_DATA_SUBSORT.MISSING;
    }

    return 0;
  }
}
